/**
 * Shared renderer for MoJ dynamic routes — mirrors PccPageBody structure
 * but tuned for legal-translation copy (per-page pricing, court audiences).
 */
import { Link, useRouterState } from "@tanstack/react-router";
import { ContentFreshness } from "@/components/ContentFreshness";
import type { MojPageContent } from "@/lib/moj/generator";
import type { MojService } from "@/data/moj";
import { RelatedClusters } from "@/components/RelatedClusters";
import { OfficialServiceSources } from "@/components/OfficialServiceSources";

export function MojPageBody(props: {
  content: MojPageContent;
  service?: MojService;
  breadcrumb?: Array<{ label: string; to?: string }>;
}) {
  const { content, service, breadcrumb } = props;
  const pathname = useRouterState({ select: (st) => st.location.pathname });
  return (
    <article className="mx-auto max-w-4xl px-4 py-10 prose prose-slate max-w-none">
      {breadcrumb && (
        <nav className="text-sm text-slate-500 mb-4" aria-label="Breadcrumb">
          {breadcrumb.map((b, i) => (
            <span key={i}>
              {i > 0 && " › "}
              {b.to ? <Link to={b.to}>{b.label}</Link> : <span>{b.label}</span>}
            </span>
          ))}
        </nav>
      )}
      <h1 className="text-3xl md:text-4xl font-bold text-slate-900">{content.h1}</h1>
      <p
        className="speakable-summary lead answer-summary text-lg text-slate-700"
        dangerouslySetInnerHTML={{ __html: content.intro }}
      />

      <section aria-labelledby="benefits">
        <h2 id="benefits">จุดเด่นบริการแปลรับรอง MoJ ของเรา</h2>
        <dl className="grid gap-4 md:grid-cols-2">
          {content.benefits.map(([k, v], i) => (
            <div key={i} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <dt className="font-semibold text-slate-900">{k}</dt>
              <dd className="text-slate-700 mt-1">{v}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section aria-labelledby="steps">
        <h2 id="steps">ขั้นตอนแปลและรับรองทีละสเต็ป</h2>
        <ol className="space-y-3">
          {content.steps.map((s, i) => (
            <li key={i} className="rounded border-l-4 border-blue-600 bg-blue-50 p-3">
              <strong className="text-blue-900">
                {i + 1}. {s.tag} —
              </strong>{" "}
              <span className="text-slate-800">{s.text}</span>
            </li>
          ))}
        </ol>
      </section>

      {content.sections.map((sec, i) => (
        <section key={i} aria-label={sec.heading}>
          <h2>{sec.heading}</h2>
          <div dangerouslySetInnerHTML={{ __html: sec.html }} />
        </section>
      ))}

      {service && (
        <section aria-labelledby="package">
          <h2 id="package">รายละเอียดแพ็กเกจ {service.nameTh}</h2>
          <ul>
            <li>ค่าบริการ: สอบถามทางโทรศัพท์ · LINE · อีเมล</li>
            <li>ระยะเวลา: {service.turnaroundDays} วันทำการ</li>
            <li>รวมบริการ: {service.includes.join(" · ")}</li>
            <li>เหมาะกับ: {service.audience.join(" · ")}</li>
            <li>ยอมรับโดย: {service.acceptedBy.join(" · ")}</li>
          </ul>
        </section>
      )}

      <section
        aria-labelledby="faq"
        style={{ contentVisibility: "auto", containIntrinsicSize: "600px 800px" }}
      >
        <h2 id="faq">คำถามที่พบบ่อย</h2>
        <div className="space-y-4">
          {content.faqs.map((f, i) => (
            <details key={i} className="rounded border border-slate-200 p-4">
              <summary className="font-semibold cursor-pointer text-slate-900">{f.q}</summary>
              <p className="mt-2 text-slate-700 faq-answer">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="mt-10 rounded-lg bg-blue-600 p-6 text-white">
        <h2 className="text-white text-2xl">พร้อมแปลรับรองกระทรวงยุติธรรมตอนนี้</h2>
        <p className="text-blue-100">
          สอบถามขอบเขตงานและขอใบเสนอราคาผ่านโทรศัพท์ · LINE @thainotarylaw · Email
        </p>
      </section>

      <OfficialServiceSources theme="moj" />
      <RelatedClusters theme="moj" />

      <p className="mt-6 text-xs text-slate-400">
        เนื้อหาถูกสร้างเฉพาะสำหรับหน้านี้ (page seed) — unique per URL • ~{content.wordCount} คำ
      </p>
      <ContentFreshness path={pathname} locale="th" />
    </article>
  );
}
